import React from 'react'
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps'
import { Maximize2 } from 'lucide-react'
import { Checkin, Feature, ManualOverrideType } from '../api/client'
import { StandardPin } from './checkin/pinStyle'
import { findLocationsFeatureId } from './LocationChips'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY as string | undefined
const MAP_ID = 'memgenie-checkin-locations'
const SINGLE_POINT_ZOOM = 14
const DEGENERATE_BOUNDS_THRESHOLD_DEG = 0.001

interface CheckinLocationsMapProps {
  checkin: Checkin
  collectionItems: Record<string, any>
  /** Page-level feature list. Used to resolve the user-global Locations
   *  feature_id without depending on the per-checkin `feature_details`,
   *  which goes stale when a parse attaches the first-ever Locations row
   *  to a previously-loaded checkin. Mirrors the chip-side fix. */
  features?: Feature[]
  /** Stable row key (`ref-{id}` or `coords-{lat}-{lng}`) of the currently
   *  selected pin, lifted to the parent so the matching chip can highlight
   *  in lockstep. `null` when nothing is selected. */
  highlightedKey?: string | null
  /** Pin click handler. Parent decides toggle/switch semantics — this just
   *  emits the row key. */
  onMarkerClick?: (key: string) => void
  /** When set, an "expand" button overlays the top-right corner. Click
   *  fires this — typically opens a larger interactive map modal. The
   *  inline map keeps cooperative gestures regardless; expansion is the
   *  designated way to actually pan/zoom around. */
  onExpand?: () => void
}

interface MarkerCoord {
  key: string
  lat: number
  lng: number
}

function extractCoords(
  checkin: Checkin,
  collectionItems: Record<string, any>,
  features?: Feature[],
): MarkerCoord[] {
  const locationsFeatureId = findLocationsFeatureId(checkin, features)
  if (locationsFeatureId == null) return []

  const coords: MarkerCoord[] = []
  for (const cf of checkin.checkin_features) {
    if (cf.feature_id !== locationsFeatureId) continue
    if (cf.manual_override === ManualOverrideType.DELETE) continue
    if (cf.reference_value != null) {
      const item = collectionItems[String(cf.reference_value)]
      const lat = item?.data?.lat
      const lng = item?.data?.lng
      if (typeof lat === 'number' && typeof lng === 'number') {
        coords.push({ key: `ref-${cf.reference_value}`, lat, lng })
      }
    } else if (cf.latitude != null && cf.longitude != null) {
      coords.push({
        key: `coords-${cf.latitude}-${cf.longitude}`,
        lat: cf.latitude,
        lng: cf.longitude,
      })
    }
  }
  return coords
}

/**
 * Build the Map's initial view from a list of points: bounds-fit when there
 * is meaningful spread, centroid-with-zoom when everything's clustered at
 * one spot. Exported so the expanded modal can reuse it on the same data
 * set and present a consistent footprint.
 */
export function computeView(coords: { lat: number; lng: number }[]): {
  defaultCenter?: { lat: number; lng: number }
  defaultZoom?: number
  defaultBounds?: google.maps.LatLngBoundsLiteral
} {
  const lats = coords.map((c) => c.lat)
  const lngs = coords.map((c) => c.lng)
  const north = Math.max(...lats)
  const south = Math.min(...lats)
  const east = Math.max(...lngs)
  const west = Math.min(...lngs)
  // One coord (or several at the same spot): bounds-fit zooms to max and
  // strips all context. Anchor on the centroid with a sane default zoom.
  if (
    north - south < DEGENERATE_BOUNDS_THRESHOLD_DEG &&
    east - west < DEGENERATE_BOUNDS_THRESHOLD_DEG
  ) {
    return {
      defaultCenter: { lat: (north + south) / 2, lng: (east + west) / 2 },
      defaultZoom: SINGLE_POINT_ZOOM,
    }
  }
  return { defaultBounds: { north, south, east, west } }
}

/**
 * Returns true when the checkin has at least one resolvable coord (saved
 * Place with lat/lng cached on its collection item, or a bare-coords pin)
 * AND the Maps API key is configured. Used by parents to decide whether to
 * lay out a map column at all — if this is false, no slot is reserved.
 */
export function hasCheckinLocationsMap(
  checkin: Checkin,
  collectionItems: Record<string, any>,
  features?: Feature[],
): boolean {
  if (!GOOGLE_MAPS_API_KEY) return false
  return extractCoords(checkin, collectionItems, features).length > 0
}

/**
 * Inline mini-map showing every coord-bearing location on a checkin, fitted
 * to the smallest bounding box that encompasses them. Renders nothing when
 * the checkin has no resolvable coords (Place refs without lat/lng yet, or
 * no Locations feature at all). The full-detail modal is still reachable
 * via the chips above; this is just an at-a-glance footprint.
 *
 * When `highlightedKey` is set, the matching pin renders amber + slightly
 * scaled up. Tapping a pin emits its row key via `onMarkerClick` so the
 * parent can decide whether to set, toggle, or switch the highlight.
 */
const CheckinLocationsMap: React.FC<CheckinLocationsMapProps> = ({
  checkin,
  collectionItems,
  features,
  highlightedKey,
  onMarkerClick,
  onExpand,
}) => {
  if (!GOOGLE_MAPS_API_KEY) return null
  const coords = extractCoords(checkin, collectionItems, features)
  if (coords.length === 0) return null
  const view = computeView(coords)
  return (
    <div className="relative rounded-md overflow-hidden border border-gray-200 h-40">
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          style={{ width: '100%', height: '100%' }}
          {...view}
          mapId={MAP_ID}
          gestureHandling="cooperative"
          disableDefaultUI
        >
          {coords.map((c) => {
            const isHighlighted = highlightedKey === c.key
            return (
              <AdvancedMarker
                key={c.key}
                position={{ lat: c.lat, lng: c.lng }}
                onClick={() => onMarkerClick?.(c.key)}
              >
                <StandardPin highlighted={isHighlighted} />
              </AdvancedMarker>
            )
          })}
        </Map>
      </APIProvider>
      {onExpand && (
        <button
          type="button"
          onClick={onExpand}
          className="absolute top-2 right-2 bg-white/90 hover:bg-white border border-gray-300 rounded-md p-1.5 shadow-sm text-gray-700 hover:text-gray-900 transition-colors"
          title="Expand map"
          aria-label="Expand map"
        >
          <Maximize2 className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}

export default CheckinLocationsMap
